import React, { FC, useState } from 'react';
import { Box, Stack } from '@mui/material';

import { Typography } from '../Typography/Typography';
import { Select, SelectOption } from '../Select/Select';
import Accordion from '../Accordion/Accordion';
import { Icon } from '../Icon/Icon';

import { accordionStyle } from './styles';

interface MealKitIngredient {
  id: string;
  name: string;
  type: string;
  isSelectable: boolean;
  quantity: string;
  unit: string;
  isIncluded: boolean;
  photoUrl?: string;
}

interface MealKitVariant {
  id: string;
  name: string;
  type: string;
  variantId: string;
  photoUrl?: string;
  quantity?: string;
  unit?: string;
}

interface DishIngredientsProps {
  isMealKit: boolean;
  // For dishes - simple string
  simpleIngredients?: string;
  // For meal kits - complex array
  mealKitIngredients?: MealKitIngredient[];
  // For variant selection in meal kits
  variants?: MealKitVariant[];
  selectedVariantId?: string;
  onVariantChange?: (variantId: string) => void;
  showVariantError?: boolean;
  isReadOnly?: boolean;
  photoUrl?: string;
  name?: string;
}

export const DishIngredients: FC<DishIngredientsProps> = ({
  isMealKit,
  simpleIngredients,
  mealKitIngredients = [],
  variants = [],
  selectedVariantId = '',
  onVariantChange,
  showVariantError = false,
  isReadOnly = false,
  photoUrl,
  name,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isIncludedExpanded, setIsIncludedExpanded] = useState(true);
  const [notIncludedExpanded, setNotIncludedExpanded] = useState(true);

  // Convert variants to select options
  const variantOptions: SelectOption[] = variants.map((variant) => ({
    label: variant.name,
    value: `${variant.variantId}`,
  }));

  // Find the selected variant
  const selectedVariant: MealKitVariant | undefined = variants.find(
    (variant) => `${variant.variantId}` === selectedVariantId
  );

  // Filter ingredients by inclusion status
  const includedIngredients = mealKitIngredients.filter(
    (ingredient) => ingredient.isIncluded
  );
  const notIncludedIngredients = mealKitIngredients.filter(
    (ingredient) => !ingredient.isIncluded
  );

  const renderIngredientImage = (item: { photoUrl?: string; name: string }) => (
    <Box
      width={60}
      height={60}
      bgcolor="grey.200"
      borderRadius={0.2}
      display="flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
    >
      {item?.photoUrl ? (
        <img
          src={item.photoUrl}
          alt={item?.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      ) : (
        <Icon name="image-outline" size="large" fill="currentColor" />
      )}
    </Box>
  );

  const renderVariantQuantity = () => {
    if (selectedVariant?.quantity && selectedVariant?.unit) {
      return (
        <Typography variant="caption" color="text.secondary">
          {selectedVariant.quantity} {selectedVariant.unit}
        </Typography>
      );
    }

    return (
      <Typography
        variant="caption"
        color="text.secondary"
        i18nKey="meals:dishDetails.portion"
      >
        1 portion
      </Typography>
    );
  };

  const renderVariantError = () => {
    if (!showVariantError || selectedVariantId) return null;

    return (
      <Typography
        variant="caption"
        color="error"
        sx={{ mt: 0.5, display: 'block' }}
        i18nKey="meals:dishDetails.variantError"
      >
        Select an option
      </Typography>
    );
  };

  const renderVariantSelector = () => {
    if (variants.length === 0) return null;

    const hasMultipleVariants = variants.length > 1;

    return (
      <Box display="flex" alignItems="center" gap={2}>
        {renderIngredientImage({
          photoUrl: selectedVariant?.photoUrl,
          name: selectedVariant?.name || '',
        })}

        <Box flex={1}>
          {hasMultipleVariants && !isReadOnly ? (
            <>
              <Select
                placeholder="Select option"
                options={variantOptions}
                value={selectedVariantId || ''}
                onChange={(event) =>
                  onVariantChange?.(event.target.value as string)
                }
                error={showVariantError && !selectedVariantId}
              />
              {renderVariantError()}
            </>
          ) : (
            <>
              <Typography variant="body2">{selectedVariant?.name}</Typography>
              {renderVariantQuantity()}
            </>
          )}
        </Box>
      </Box>
    );
  };

  if (!isMealKit && simpleIngredients) {
    // Simple dish ingredients
    return (
      <>
        <Accordion
          title="Included in your delivery"
          expanded={isIncludedExpanded}
          onChange={() => setIsIncludedExpanded(!isIncludedExpanded)}
          accordionStyle={accordionStyle}
          i18nKeyTitle="meals:dishDetails.includedInYourDelivery"
        >
          <Stack spacing={2}>
            <Box display="flex" alignItems="center" gap={2}>
              {renderIngredientImage({
                photoUrl,
                name: name || '',
              })}
              <Box flex={1}>
                <Typography variant="body1">{name}</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  i18nKey="meals:dishDetails.fullyPreparedMeal"
                >
                  Fully prepared meal
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Accordion>

        <Accordion
          title="Meal ingredients"
          expanded={isExpanded}
          onChange={() => setIsExpanded(!isExpanded)}
          accordionStyle={accordionStyle}
          i18nKeyTitle="meals:dishDetails.mealIngredients"
        >
          <Typography variant="body2" color="text.secondary">
            {simpleIngredients}
          </Typography>
        </Accordion>
      </>
    );
  }

  if (isMealKit) {
    return (
      <Stack spacing={2} data-testid="ingredients-section">
        {/* Included in your kit accordion */}
        <Accordion
          title="Included in your kit"
          expanded={isExpanded}
          onChange={() => setIsExpanded(!isExpanded)}
          accordionStyle={accordionStyle}
          i18nKeyTitle="meals:dishDetails.includedInYourKit"
        >
          <Stack spacing={2}>
            {/* Variant selector - image left of dropdown */}
            {renderVariantSelector()}

            {/* Included ingredients */}
            {includedIngredients.map((ingredient) => (
              <Box
                key={ingredient.id}
                display="flex"
                alignItems="center"
                gap={2}
              >
                {renderIngredientImage(ingredient)}
                <Box flex={1}>
                  <Typography variant="body2">{ingredient.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ingredient.quantity} {ingredient.unit}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Accordion>

        {/* Ingredients not included accordion */}
        {notIncludedIngredients.length > 0 && (
          <Accordion
            title="Ingredients not included"
            expanded={notIncludedExpanded}
            onChange={() => setNotIncludedExpanded(!notIncludedExpanded)}
            accordionStyle={accordionStyle}
            i18nKeyTitle="meals:dishDetails.notIncludedIngredients"
          >
            <Stack spacing={1} sx={{ width: '100%' }}>
              {notIncludedIngredients.map((ingredient) => (
                <Box
                  key={ingredient.id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Typography variant="body2">•</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ingredient.quantity} {ingredient.unit}
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {ingredient.name}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Accordion>
        )}
      </Stack>
    );
  }

  return null;
};

export default DishIngredients;
